import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tabs,
  Tab,
  Button,
  Paper,
  InputAdornment,
} from '@mui/material';
import { Search, Mail, Users } from 'lucide-react';
import { toast } from '../../../ui/toast';
import { DIALOG_TYPE } from '../../../constants/DialogTypes';
import { DataTable, Dialog, NameEmailAvatar } from '../../../ui';
import {
  getInviteList,
  removeFromInviteList,
  sendInvites,
  sendInvitesByEmail,
} from '../../../utils/api_service/JobOpenings';
import { getJobPostList } from '../../../utils/api_service/JobPosting';
import {
  InviteListItem,
  InviteListResponse,
  JobPostingData,
} from '../../../utils/types/JobOpenings';

interface ViewInviteListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Extended invite list item that also carries its job info for display
interface EnrichedInviteItem extends InviteListItem {
  jobTitle: string;
  jobOpeningId: string;
}

interface InviteListApiItem extends InviteListItem {
  jobOpeningId?: string;
  job_opening_id?: string;
  jobTitle?: string;
  job_title?: string;
}

const PAGE_SIZE = 10;
const INVITE_API_PAGE_SIZE = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const JOB_TITLE_UNAVAILABLE = 'Currently unavailable';

export const ViewInviteListModal: React.FC<ViewInviteListModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'list' | 'email'>('list');
  const [jobOpenings, setJobOpenings] = useState<JobPostingData[]>([]);
  const [allItems, setAllItems] = useState<EnrichedInviteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    search: '',
    applied: '',
    invited: '',
    jobId: '',
  });

  // Email invite tab state
  const [emailInput, setEmailInput] = useState('');
  const [emailJobId, setEmailJobId] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const parsedEmails = useMemo(
    () =>
      emailInput
        .split(/[;,]/)
        .map((e) => e.trim())
        .filter((e) => e.length > 0),
    [emailInput],
  );

  const invalidParsedEmails = useMemo(
    () => parsedEmails.filter((e) => !EMAIL_REGEX.test(e)),
    [parsedEmails],
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const jobsRes = await getJobPostList([], [], [], [], 1, 200);
      if (!jobsRes.ok) throw new Error('jobs');
      const jobsData = await jobsRes.json();
      const jobs: JobPostingData[] = jobsData.results ?? [];
      setJobOpenings(jobs);

      const invitesRes = await getInviteList(undefined, 1, INVITE_API_PAGE_SIZE);
      if (!invitesRes.ok) throw new Error('invites');
      const invitesData: InviteListResponse = await invitesRes.json();

      const totalPages = Math.ceil(
        (invitesData.total || 0) / INVITE_API_PAGE_SIZE,
      );
      const remainingResults =
        totalPages > 1
          ? await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              getInviteList(undefined, i + 2, INVITE_API_PAGE_SIZE)
                .then((res) =>
                  res.ok ? res.json() : Promise.resolve({ results: [] }),
                )
                .then((data: InviteListResponse) => data.results)
                .catch(() => [] as InviteListApiItem[]),
            ),
          )
          : [];

      const allInviteResults: InviteListApiItem[] = [
        ...(invitesData.results as InviteListApiItem[]),
        ...remainingResults.flat(),
      ];

      const jobMap = new Map(
        jobs
          .filter((j) => j.jobOpeningId)
          .map((j) => [j.jobOpeningId as string, j]),
      );

      const enriched = allInviteResults.map(
        (item) => {
          const mappedJobOpeningId =
            item.jobOpeningId ?? item.job_opening_id ?? '';
          const jobMeta = mappedJobOpeningId
            ? jobMap.get(mappedJobOpeningId)
            : undefined;

          return {
            ...item,
            jobOpeningId: mappedJobOpeningId,
            jobTitle:
              item.jobTitle ??
              item.job_title ??
              jobMeta?.jobTitle ??
              JOB_TITLE_UNAVAILABLE,
          };
        },
      );

      setAllItems(enriched);
    } catch {
      toast.error('Failed to load invite lists', { position: 'bottom-center' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds(new Set());
    setFilters({ search: '', applied: '', invited: '', jobId: '' });
    setPage(1);
    setActiveTab('list');
    setEmailInput('');
    setEmailJobId('');
    setEmailSending(false);
    fetchAll();
  }, [isOpen, fetchAll]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setSelectedIds(new Set());
  };

  const filteredItems = useMemo(() => {
    return allItems.filter((c) => {
      const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
      const matchesSearch =
        !filters.search ||
        fullName.includes(filters.search.toLowerCase()) ||
        c.email.toLowerCase().includes(filters.search.toLowerCase());
      const matchesApplied =
        !filters.applied ||
        (filters.applied === 'applied' && c.applied) ||
        (filters.applied === 'not-applied' && !c.applied);
      const matchesInvited =
        !filters.invited ||
        (filters.invited === 'invited' && c.invited) ||
        (filters.invited === 'pending' && !c.invited);
      const matchesJob = !filters.jobId || c.jobOpeningId === filters.jobId;
      return matchesSearch && matchesApplied && matchesInvited && matchesJob;
    });
  }, [allItems, filters]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page]);

  const handleSelectRow = (_: number, row: EnrichedInviteItem) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(
      selectedIds.size === pagedItems.length
        ? new Set()
        : new Set(pagedItems.map((c) => c.id)),
    );
  };

  const handleSendSelected = async () => {
    if (selectedIds.size === 0) return;
    const byJob = new Map<string, string[]>();
    pagedItems
      .filter((c) => selectedIds.has(c.id))
      .forEach((c) => {
        const list = byJob.get(c.jobOpeningId) ?? [];
        list.push(c.id);
        byJob.set(c.jobOpeningId, list);
      });
    try {
      const responses = await Promise.all(
        Array.from(byJob.entries()).map(([jobId, ids]) =>
          sendInvites(jobId, { inviteIds: ids }),
        ),
      );
      const failed = responses.filter((r) => !r.ok);
      if (failed.length > 0) {
        const err = await failed[0].json().catch(() => null);
        toast.error(err?.error ?? 'Failed to send invites', {
          position: 'bottom-center',
        });
        return;
      }
      toast.success(`Invites sent to ${selectedIds.size} candidates`);
      setSelectedIds(new Set());
      fetchAll();
    } catch {
      toast.error('Error sending invites');
    }
  };

  const handleSendAll = async () => {
    const knownJobIds = new Set(
      allItems.map((item) => item.jobOpeningId).filter(Boolean),
    );
    const jobIds = filters.jobId
      ? knownJobIds.has(filters.jobId)
        ? [filters.jobId]
        : []
      : Array.from(knownJobIds);

    if (jobIds.length === 0) {
      toast.info('No invite list entries found to send', {
        position: 'bottom-center',
      });
      return;
    }
    try {
      const responses = await Promise.all(
        jobIds.map((id) => sendInvites(id, { sendAll: true })),
      );
      const failed = responses.filter((r) => !r.ok);
      if (failed.length > 0) {
        const err = await failed[0].json().catch(() => null);
        toast.error(err?.error ?? 'Failed to send invites', {
          position: 'bottom-center',
        });
        return;
      }
      toast.success('Invites sent to all pending candidates');
      fetchAll();
    } catch {
      toast.error('Error sending invites');
    }
  };

  const handleRemove = async () => {
    if (selectedIds.size === 0) return;
    const toRemove = pagedItems.filter(
      (c) => selectedIds.has(c.id) && !c.invited,
    );
    if (toRemove.length === 0) {
      toast.warning('Cannot remove candidates who have already been invited');
      return;
    }
    try {
      await Promise.all(
        toRemove.map((c) => removeFromInviteList(c.jobOpeningId, c.id)),
      );
      toast.success(
        `Removed ${toRemove.length} candidate${
          toRemove.length !== 1 ? 's' : ''
        } from list`,
      );
      setSelectedIds(new Set());
      fetchAll();
    } catch {
      toast.error('Error removing candidates');
    }
  };

  const handleSendByEmail = async () => {
    const emails = parsedEmails;
    const invalidEmails = invalidParsedEmails;
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email(s): ${invalidEmails.join(', ')}`);
      return;
    }
    if (emails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }
    if (!emailJobId) {
      toast.error('Please select a job opening');
      return;
    }
    setEmailSending(true);
    try {
      const res = await sendInvitesByEmail(emailJobId, { emails });
      if (res.ok) {
        toast.success(
          `Invites sent to ${emails.length} candidate${
            emails.length !== 1 ? 's' : ''
          }`,
        );
        setEmailInput('');
      } else {
        toast.error('Failed to send invites');
      }
    } catch {
      toast.error('Error sending invites');
    } finally {
      setEmailSending(false);
    }
  };

  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);

  const columns = [
    {
      key: 'name',
      label: 'Candidate',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <NameEmailAvatar
            name={`${row.firstName} ${row.lastName}`}
            email={row.email}
            avatar=""
          />
        </Stack>
      ),
    },
    {
      key: 'jobTitle',
      label: 'Job Opening',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {row.jobTitle}
        </Typography>
      ),
    },
    {
      key: 'addedAt',
      label: 'Added On',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {row.addedAt
            ? new Date(row.addedAt * 1000).toLocaleDateString()
            : '—'}
        </Typography>
      ),
    },
    {
      key: 'invitedAt',
      label: 'Invited On',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {row.invited && row.invitedAt
            ? new Date(row.invitedAt * 1000).toLocaleDateString()
            : '—'}
        </Typography>
      ),
    },
    {
      key: 'applied',
      label: 'Applied',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <Chip
          label={row.applied ? 'Yes' : 'No'}
          size="small"
          color={row.applied ? 'success' : 'default'}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.625rem', fontWeight: 700 }}
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <Chip
          label={row.invited ? 'Invited' : 'Pending'}
          size="small"
          color={row.invited ? 'success' : 'warning'}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.625rem', fontWeight: 700 }}
        />
      ),
    },
  ];

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Invite List"
      dialogType={DIALOG_TYPE.SUCCESS}
      maxWidth="1400px"
      cancelButtonLable="Close"
      confirmButtonLable=""
      handleApply={onClose}
      noScroll={true}
    >
      <Stack spacing={2.5}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{ minHeight: 40 }}
          >
            <Tab
              value="list"
              label="Current Invite List"
              icon={<Users size={16} />}
              iconPosition="start"
              sx={{ minHeight: 40, fontSize: '0.8125rem', fontWeight: 700, textTransform: 'none' }}
            />
            <Tab
              value="email"
              label="Send Invites by Email"
              icon={<Mail size={16} />}
              iconPosition="start"
              sx={{ minHeight: 40, fontSize: '0.8125rem', fontWeight: 700, textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {activeTab === 'list' && (
          <>
            {/* Filters */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Search"
                  placeholder="Name or email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                />

                <FormControl size="small" fullWidth disabled={loading}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Job Opening</InputLabel>
                  <Select
                    value={filters.jobId}
                    label="Job Opening"
                    onChange={(e) => handleFilterChange('jobId', e.target.value)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All Jobs</MenuItem>
                    {jobOpenings
                      .filter((j) => j.jobOpeningId)
                      .map((j) => {
                        const parts = [j.jobTitle ?? JOB_TITLE_UNAVAILABLE];
                        if (j.location) parts.push(j.location);
                        if (j.jobReferenceNumber) parts.push(`[${j.jobReferenceNumber}]`);
                        return (
                          <MenuItem key={j.jobOpeningId} value={j.jobOpeningId} sx={{ fontSize: '0.75rem' }}>
                            {parts.join(' · ')}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Application Status</InputLabel>
                  <Select
                    value={filters.applied}
                    label="Application Status"
                    onChange={(e) => handleFilterChange('applied', e.target.value)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                    <MenuItem value="applied" sx={{ fontSize: '0.75rem' }}>Applied</MenuItem>
                    <MenuItem value="not-applied" sx={{ fontSize: '0.75rem' }}>Not Applied</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Invite Status</InputLabel>
                  <Select
                    value={filters.invited}
                    label="Invite Status"
                    onChange={(e) => handleFilterChange('invited', e.target.value)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                    <MenuItem value="invited" sx={{ fontSize: '0.75rem' }}>Invited</MenuItem>
                    <MenuItem value="pending" sx={{ fontSize: '0.75rem' }}>Pending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            {/* Actions */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {selectedIds.size > 0 ? `${selectedIds.size} selected · ` : ''}
                {filteredItems.length} total
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleRemove}
                  disabled={selectedIds.size === 0 || loading}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                >
                  Remove
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSendSelected}
                  disabled={selectedIds.size === 0 || loading}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                >
                  Send Selected
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={handleSendAll}
                  disabled={loading}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                >
                  Send All Pending
                </Button>
              </Stack>
            </Stack>

            {/* Table */}
            <Box sx={{ height: 440, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <DataTable
                data={pagedItems}
                columns={columns}
                isLoading={loading}
                hasCheckBox
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                isRowSelected={(_: number, row: EnrichedInviteItem) =>
                  selectedIds.has(row.id)
                }
                isAllSelected={
                  pagedItems.length > 0 &&
                  selectedIds.size === pagedItems.length
                }
                isIndeterminate={
                  selectedIds.size > 0 && selectedIds.size < pagedItems.length
                }
                rowsPerPage={PAGE_SIZE}
                currentPage={page}
                totalPages={totalPages}
                totalRecords={filteredItems.length}
                onPageChange={(p: number) => setPage(p)}
              />
            </Box>
          </>
        )}

        {activeTab === 'email' && (
          <Stack spacing={2.5} sx={{ py: 1 }}>
            <FormControl size="small" fullWidth disabled={loading}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Job Opening *</InputLabel>
              <Select
                value={emailJobId}
                label="Job Opening *"
                onChange={(e) => setEmailJobId(e.target.value)}
                sx={{ fontSize: '0.75rem' }}
              >
                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Select a job opening...</MenuItem>
                {jobOpenings
                  .filter((j) => j.jobOpeningId)
                  .map((j) => {
                    const parts = [j.jobTitle ?? JOB_TITLE_UNAVAILABLE];
                    if (j.location) parts.push(j.location);
                    if (j.jobReferenceNumber) parts.push(`[${j.jobReferenceNumber}]`);
                    return (
                      <MenuItem key={j.jobOpeningId} value={j.jobOpeningId} sx={{ fontSize: '0.75rem' }}>
                        {parts.join(' · ')}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>

            <Stack spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Email Addresses *
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Paste semi-colon separated email addresses. Each candidate will receive a pre-qualification invite link.
              </Typography>
              <TextField
                multiline
                rows={6}
                fullWidth
                placeholder="e.g. alice@example.com; bob@example.com; carol@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value.replace(/,/g, ';'))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
              />
              {emailInput.trim() && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {parsedEmails.length} email(s) detected
                </Typography>
              )}

              {parsedEmails.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {parsedEmails.map((email, index) => {
                    const isValid = EMAIL_REGEX.test(email);
                    return (
                      <Chip
                        key={`${email}-${index}`}
                        label={email}
                        size="small"
                        color={isValid ? 'success' : 'error'}
                        variant="outlined"
                        sx={{ fontSize: '0.6875rem' }}
                      />
                    );
                  })}
                </Stack>
              )}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleSendByEmail}
                disabled={emailSending || parsedEmails.length === 0 || !emailJobId}
                sx={{ minWidth: 140, fontWeight: 700, textTransform: 'none', fontSize: '0.8125rem' }}
              >
                {emailSending ? 'Sending...' : 'Send Invites'}
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
};
